// This file is automatically generated. Do not edit it directly.
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { getEnv, getEnvError, isEnvValid } from '@/config/env';

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

// Get base URL for redirects - always use window.location.origin for production correctness
// Never use hardcoded URLs (no localhost, no hardcoded domains)
const getBaseUrl = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  // Fallback for SSR (shouldn't happen in SPA, but defensive)
  return '';
};

// Lazy initialization to prevent module-level errors before React ErrorBoundary can catch them
// This allows EnvErrorBoundary to render and show a friendly error message instead of crashing
let supabaseInstance: SupabaseClient<Database> | null = null;
let initError: Error | null = null;

function getSupabaseClient(): SupabaseClient<Database> {
  // If already initialized, return cached instance
  if (supabaseInstance) {
    return supabaseInstance;
  }

  // If initialization previously failed, throw the cached error
  if (initError) {
    throw initError;
  }

  // Check if env is valid before trying to get it
  // This prevents throwing during module import, allowing React to render ErrorBoundary
  if (!isEnvValid()) {
    const envErr = getEnvError();
    const message =
      envErr ||
      'VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY (or VITE_SUPABASE_PUBLISHABLE_KEY) are required.';
    console.error(
      '[Supabase] Environment validation failed. Edge Functions and API calls will not work.',
      message
    );
    initError = new Error(
      'Supabase client cannot be initialized: ' + message + ' Check project Environment Variables.'
    );
    throw initError;
  }

  try {
    const env = getEnv();
    
    // DEV ONLY: Diagnostic logging - verify API key is present (first 6 chars only)
    if (import.meta.env.DEV) {
      console.log('[Supabase Client] Initializing:', {
        VITE_SUPABASE_URL: !!env.supabaseUrl,
        VITE_SUPABASE_ANON_KEY: !!env.supabasePublishableKey,
        anonKeyPrefix: env.supabasePublishableKey?.substring(0, 6) || 'missing',
        url: env.supabaseUrl,
      });
    }
    
    // Verify key is not empty before creating client
    if (!env.supabasePublishableKey || env.supabasePublishableKey.trim().length === 0) {
      throw new Error('VITE_SUPABASE_ANON_KEY (or VITE_SUPABASE_PUBLISHABLE_KEY) is empty. Please check Vercel environment variables.');
    }
    
    // CRITICAL: Explicitly set global headers to ensure apikey is always sent
    // Even though createClient should add it automatically, we set it explicitly as a safeguard
    // This prevents 400 errors if the automatic header injection fails
    supabaseInstance = createClient<Database>(env.supabaseUrl, env.supabasePublishableKey, {
      global: {
        headers: {
          'apikey': env.supabasePublishableKey,
        },
      },
      auth: {
        storage: typeof window !== 'undefined' ? localStorage : undefined,
        persistSession: true,
        autoRefreshToken: true,
        redirectTo: getBaseUrl(),
      }
    });
    
    // DEV ONLY: Verify the client was created with the key
    if (import.meta.env.DEV) {
      console.log('[Supabase Client] Client created successfully with apikey header');
    }
    return supabaseInstance;
  } catch (error) {
    initError = error instanceof Error ? error : new Error(String(error));
    throw initError;
  }
}

// Export as a Proxy to maintain the same API while initializing lazily
// This ensures all existing imports continue to work without changes
export const supabase = new Proxy({} as SupabaseClient<Database>, {
  get(_target, prop) {
    try {
      const client = getSupabaseClient();
      const value = (client as any)[prop];
      // If it's a function, bind it to the client to maintain 'this' context
      if (typeof value === 'function') {
        return value.bind(client);
      }
      return value;
    } catch (error) {
      // If client initialization fails, log and rethrow
      // This allows ErrorBoundary to catch it gracefully
      console.error(`[Supabase] Error accessing ${String(prop)}:`, error);
      throw error;
    }
  }
}) as SupabaseClient<Database>;